import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import { OperationCountsSummary, OperationProgressBar, OperationStateBadge } from '@/components/operations/OperationProgress'
import { useOperations } from '@/hooks/useOperations'
import type { Operation, OperationCounts } from '@/lib/api/schemas/operations.schema'
import { isActiveOperation, operationTitle, progressPercent } from '@/lib/operations/labels'

export const BUBBLE_STORAGE_KEY = 'almaz.operations-bubble.v1'
const EDGE_GAP = 8
const DRAG_THRESHOLD = 4

type Point = { x: number; y: number }

const StoredBubbleSchema = z.object({
  position: z.object({ x: z.number().finite(), y: z.number().finite() }).nullable(),
  collapsed: z.boolean(),
})

type StoredBubble = z.infer<typeof StoredBubbleSchema>

function readStoredBubble(): StoredBubble {
  try {
    const raw = window.localStorage.getItem(BUBBLE_STORAGE_KEY)
    if (raw === null) return { position: null, collapsed: false }
    const parsed = StoredBubbleSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : { position: null, collapsed: false }
  } catch {
    return { position: null, collapsed: false }
  }
}

function writeStoredBubble(value: StoredBubble): void {
  try {
    window.localStorage.setItem(BUBBLE_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Private windows and blocked storage only lose the remembered position.
  }
}

export function clampToViewport(point: Point, size: { width: number; height: number }): Point {
  // Floored bounds keep a fractional-size box from rounding past the edge gap.
  const maxX = Math.max(EDGE_GAP, Math.floor(window.innerWidth - size.width - EDGE_GAP))
  const maxY = Math.max(EDGE_GAP, Math.floor(window.innerHeight - size.height - EDGE_GAP))
  return {
    x: Math.min(Math.max(EDGE_GAP, Math.round(point.x)), maxX),
    y: Math.min(Math.max(EDGE_GAP, Math.round(point.y)), maxY),
  }
}

function totals(operations: readonly Operation[]): OperationCounts {
  return operations.reduce<OperationCounts>((sum, { counts }) => ({
    total: sum.total + counts.total,
    pending: sum.pending + counts.pending,
    running: sum.running + counts.running,
    ok: sum.ok + counts.ok,
    failed: sum.failed + counts.failed,
    skipped: sum.skipped + counts.skipped,
    withWarnings: sum.withWarnings + counts.withWarnings,
  }), { total: 0, pending: 0, running: 0, ok: 0, failed: 0, skipped: 0, withWarnings: 0 })
}

function activeSummary(operations: readonly Operation[]): string {
  const running = operations.filter((operation) => operation.state === 'running').length
  const queued = operations.filter((operation) => operation.state === 'queued').length
  if (running === 0 && queued === 0) return 'Все операции завершены'
  const parts = [running > 0 ? `выполняется ${running}` : null, queued > 0 ? `в очереди ${queued}` : null]
  return parts.filter((part) => part !== null).join(' · ')
}

type DragState = { pointerId: number; start: Point; origin: Point; size: { width: number; height: number }; moved: boolean }

export function OperationsBubble() {
  const { operations, finished, error, dismissFinished } = useOperations({ scope: 'active', trackFinished: true })
  const [stored] = useState(readStoredBubble)
  const [position, setPosition] = useState<Point | null>(stored.position)
  const [collapsed, setCollapsed] = useState(stored.collapsed)
  const [dragging, setDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const suppressClickRef = useRef(false)

  const tracked = useMemo(() => {
    const activeIds = new Set(operations.map((operation) => operation.id))
    return [...operations, ...finished.filter((operation) => !activeIds.has(operation.id))]
  }, [finished, operations])
  const anyActive = tracked.some(isActiveOperation)
  const sum = totals(tracked)
  const percent = progressPercent(sum)
  const visible = tracked.length > 0

  const reclamp = useCallback(() => {
    const element = containerRef.current
    if (element === null) return
    const rect = element.getBoundingClientRect()
    setPosition((current) => {
      if (current === null) return current
      const next = clampToViewport(current, rect)
      return next.x === current.x && next.y === current.y ? current : next
    })
  }, [])

  useLayoutEffect(() => {
    if (visible) reclamp()
  }, [collapsed, reclamp, tracked.length, visible])

  useEffect(() => {
    window.addEventListener('resize', reclamp)
    return () => window.removeEventListener('resize', reclamp)
  }, [reclamp])

  const persist = (next: Partial<StoredBubble>) => {
    writeStoredBubble({ position, collapsed, ...next })
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0 || containerRef.current === null) return
    // Capturing on the header would retarget the click away from its own buttons.
    const control = event.target instanceof Element ? event.target.closest('button, a') : null
    if (control !== null && control !== event.currentTarget) return
    suppressClickRef.current = false
    const rect = containerRef.current.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      start: { x: event.clientX, y: event.clientY },
      origin: { x: rect.left, y: rect.top },
      size: { width: rect.width, height: rect.height },
      moved: false,
    }
    const target = event.currentTarget
    if (typeof target.setPointerCapture === 'function') {
      try { target.setPointerCapture(event.pointerId) } catch { /* capture is an enhancement only */ }
    }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    if (drag === null || drag.pointerId !== event.pointerId) return
    const dx = event.clientX - drag.start.x
    const dy = event.clientY - drag.start.y
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
    if (!drag.moved) {
      drag.moved = true
      setDragging(true)
    }
    setPosition(clampToViewport({ x: drag.origin.x + dx, y: drag.origin.y + dy }, drag.size))
  }

  const endDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    if (drag === null || drag.pointerId !== event.pointerId) return
    dragRef.current = null
    const target = event.currentTarget
    if (typeof target.hasPointerCapture === 'function' && target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId)
    if (!drag.moved) return
    suppressClickRef.current = true
    setDragging(false)
    const next = clampToViewport({ x: drag.origin.x + event.clientX - drag.start.x, y: drag.origin.y + event.clientY - drag.start.y }, drag.size)
    setPosition(next)
    persist({ position: next })
  }

  const dragHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  }

  const toggleCollapsed = (next: boolean) => {
    setCollapsed(next)
    persist({ collapsed: next })
  }

  if (!visible) return null

  const placement = position === null
    ? 'right-4 bottom-[calc(96px+env(safe-area-inset-bottom))]'
    : ''

  return (
    <div
      ref={containerRef}
      data-testid="operations-bubble"
      data-collapsed={collapsed ? 'true' : 'false'}
      data-dragging={dragging ? 'true' : 'false'}
      className={`fixed z-[75] ${placement}`}
      style={position === null ? undefined : { left: position.x, top: position.y }}
      onClickCapture={(event) => {
        if (!suppressClickRef.current) return
        suppressClickRef.current = false
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      {collapsed ? (
        <button
          type="button"
          {...dragHandlers}
          onClick={() => toggleCollapsed(false)}
          aria-label={`Операции: обработано ${percent}%, активных ${tracked.filter(isActiveOperation).length}. Развернуть`}
          className={`relative grid size-[56px] touch-none select-none place-items-center rounded-full bg-onyx text-ivory shadow-xl shadow-onyx/25 ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
          <svg aria-hidden="true" viewBox="0 0 56 56" className="absolute inset-0 size-[56px] -rotate-90">
            <circle cx="28" cy="28" r="24" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="4" />
            <circle
              cx="28" cy="28" r="24" fill="none" strokeWidth="4" strokeLinecap="round"
              className={sum.failed > 0 ? 'stroke-st-unpaid' : 'stroke-gold'}
              strokeDasharray={2 * Math.PI * 24}
              strokeDashoffset={2 * Math.PI * 24 * (1 - percent / 100)}
            />
          </svg>
          <span className="relative text-[13px] font-semibold tab-nums">{percent}%</span>
          <span className="absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full bg-gold px-1 text-[11px] font-semibold text-onyx tab-nums">{tracked.length}</span>
        </button>
      ) : (
        <section aria-label="Операции" className="flex max-h-[min(70dvh,560px)] w-[min(360px,calc(100vw-16px))] flex-col overflow-hidden rounded-2xl border border-rule bg-paper shadow-2xl shadow-onyx/15">
          <header
            {...dragHandlers}
            data-testid="operations-bubble-handle"
            className={`flex touch-none select-none items-center gap-2 border-b border-rule px-3 py-2.5 ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4 shrink-0 text-ink-faint" fill="currentColor"><circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" /><circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" /><circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" /></svg>
            <div className="min-w-0 flex-1">
              <h2 className="text-[14px] font-semibold leading-5 text-ink">Операции · {percent}%</h2>
              <p className="truncate text-[11px] leading-4 text-ink-muted">{activeSummary(tracked)}</p>
            </div>
            <button type="button" onClick={() => toggleCollapsed(true)} aria-label="Свернуть" title="Свернуть" className="grid size-9 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-bone hover:text-ink">
              <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 12h12" /></svg>
            </button>
            {anyActive ? null : (
              <button type="button" onClick={dismissFinished} aria-label="Закрыть" title="Закрыть" className="grid size-9 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-bone hover:text-ink">
                <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
              </button>
            )}
          </header>
          <ul className="min-h-0 flex-1 divide-y divide-rule overflow-y-auto">
            {tracked.map((operation) => (
              <li key={operation.id} data-operation-id={operation.id} className="space-y-1.5 px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="min-w-0 text-[13px] font-medium leading-5 text-ink">{operationTitle(operation)}</span>
                  <OperationStateBadge state={operation.state} />
                </div>
                <OperationProgressBar operation={operation} />
                <OperationCountsSummary counts={operation.counts} />
              </li>
            ))}
          </ul>
          {error ? <p role="alert" className="border-t border-rule bg-st-unpaid/5 px-3 py-2 text-[12px] text-st-unpaid">{error}</p> : null}
          <footer className="flex items-center justify-between gap-2 border-t border-rule px-3 py-2">
            <span className="text-[11px] leading-4 text-ink-muted">{anyActive ? 'Закрыть можно после завершения всех операций' : 'Операции завершены'}</span>
            <Link to="/operations" className="shrink-0 text-[12px] font-semibold text-gold-deep hover:text-ink">Все операции</Link>
          </footer>
        </section>
      )}
    </div>
  )
}
